"use client";

import type { CSSProperties } from "react";

export type PlayableCanvasVariant = "assessment" | "calculator" | "checklist" | "path_selector";

export type PlayableCanvasNode = {
  readonly id: string;
  readonly label: string;
  readonly detail?: string;
  readonly value?: string | number;
  readonly active?: boolean;
  readonly complete?: boolean;
};

type PlayableExperienceCanvasProps = {
  readonly id: string;
  readonly variant: PlayableCanvasVariant;
  readonly ariaLabel: string;
  readonly progress: number;
  readonly primaryLabel: string;
  readonly secondaryLabel?: string;
  readonly nodes: readonly PlayableCanvasNode[];
  readonly tracks?: readonly PlayableCanvasNode[];
};

const clampProgress = (value: number) => Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));

function stateFor(node: PlayableCanvasNode) {
  if (node.complete) {
    return "complete";
  }

  if (node.active) {
    return "active";
  }

  return "idle";
}

export default function PlayableExperienceCanvas({
  id,
  variant,
  ariaLabel,
  progress,
  primaryLabel,
  secondaryLabel,
  nodes,
  tracks = [],
}: PlayableExperienceCanvasProps) {
  const safeProgress = clampProgress(progress);
  const safeNodes = nodes.filter((node) => node.label).slice(0, 6);
  const safeTracks = tracks.filter((track) => track.label || track.value !== undefined).slice(0, 4);
  const style = {
    "--playable-progress": `${safeProgress}%`,
    "--playable-progress-ratio": String(safeProgress / 100),
  } as CSSProperties;

  return (
    <aside
      className={`gamification-tool__canvas playable-experience playable-experience--${variant}`}
      aria-label={ariaLabel}
      data-playable-experience={id}
      data-playable-variant={variant}
      data-playable-progress={Math.round(safeProgress)}
      style={style}
    >
      <div className="playable-experience__stage" aria-hidden="true">
        <span className="playable-experience__rail playable-experience__rail--back" />
        <span className="playable-experience__rail playable-experience__rail--front" />
        <span className="playable-experience__scanline" />
        <span className="playable-experience__cursor" />
        <div className="playable-experience__node-map">
          {safeNodes.map((node, index) => (
            <span
              className="playable-experience__node"
              data-playable-node-state={stateFor(node)}
              key={node.id}
              style={{ "--playable-node-index": String(index) } as CSSProperties}
            >
              <span className="playable-experience__node-mark" />
              <span className="playable-experience__node-label">{node.label}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="playable-experience__copy">
        <h3>{primaryLabel}</h3>
        {secondaryLabel ? <p>{secondaryLabel}</p> : null}
      </div>

      {safeTracks.length > 0 ? (
        <ol className="playable-experience__tracks" aria-hidden="true">
          {safeTracks.map((track) => (
            <li key={track.id} data-playable-track-state={stateFor(track)}>
              <span>{track.label}</span>
              {track.value !== undefined ? <strong>{track.value}</strong> : null}
            </li>
          ))}
        </ol>
      ) : null}
    </aside>
  );
}
